import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { NotificationService } from '@shared/services/notification.service';
import { IScanningPrefix, ScanningPrefixService } from '@shared/services/scanning-prefix.service';
import { Subject, takeUntil } from 'rxjs';
import { CreateScanningPrefixComponent } from './create-scanning-prefix/create-scanning-prefix.component';
import { DeleteScanningPrefixComponent } from './delete-scanning-prefix/delete-scanning-prefix.component';
import { UpdateScanningPrefixComponent } from './update-scanning-prefix/update-scanning-prefix.component';

interface IScanningPrefixView {
	prefix: string;
	scanningPrefix: IScanningPrefix;
}

@Component({
	selector: 'app-scanning-prefix',
	templateUrl: './scanning-prefix.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ScanningPrefixComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['prefix', 'menu'];
	dataSource = new MatTableDataSource<IScanningPrefixView>();
	isLoading = false;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.scanning-prefix.buttons.createbtn',
			click: () => this.openCreate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private service: ScanningPrefixService,
	) {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'prefix', start: 'asc', disableClear: true });
		}
	}

	private getTableViewModel(prefixes: IScanningPrefix[]): IScanningPrefixView[] {
		return (
			prefixes?.map((scanningPrefix) => {
				return {
					prefix: scanningPrefix.prefix,
					scanningPrefix,
				};
			}) ?? []
		);
	}

	updateViewModel(prefix: IScanningPrefix) {
		if (!prefix) {
			return;
		}

		const item = this.getTableViewModel([prefix])[0];
		const idx = this.dataSource.data.findIndex((x) => x.scanningPrefix.id === item.scanningPrefix.id);
		if (idx > -1) {
			this.dataSource.data[idx] = item;
		} else {
			this.dataSource.data.push(item);
		}
		this.dataSource.data = [...this.dataSource.data];
	}

	deleteViewModel(id: string): void {
		if (!id || !this.dataSource?.data?.length) {
			return;
		}

		this.dataSource.data = this.dataSource.data.filter((x) => x.scanningPrefix.id !== id);
	}

	updateList() {
		if (this.isLoading) {
			return;
		}

		this.isLoading = true;
		this.service.getAll().subscribe((prefix) => {
			this.dataSource.data = this.getTableViewModel(prefix);
			this.dataSource.sort = this.sort;
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	openCreate() {
		const dialogRef = this.dialog.open(CreateScanningPrefixComponent, {
			disableClose: true,
			width: '450px',
		});

		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe((response) => {
			this.updateList();
			this.notificationService.notify(['mes.scanning-prefix.notifications.create.header'], ['mes.scanning-prefix.notifications.create.content', { prefix: response.result.prefix }], 'success');
		});
	}

	openEdit(prefix: IScanningPrefix | null) {
		const dialogRef = this.dialog.open(UpdateScanningPrefixComponent, {
			disableClose: true,
			width: '450px',
			data: prefix,
		});

		dialogRef.componentInstance.updated.pipe(takeUntil(this.destroy$)).subscribe((response) => {
			this.updateList();
			this.notificationService.notify(['mes.scanning-prefix.notifications.update.header'], ['mes.scanning-prefix.notifications.update.content', { prefix: response.result.prefix }], 'success');
		});
	}

	openDelete(item: IScanningPrefix) {
		const dialogRef = this.dialog.open(DeleteScanningPrefixComponent, {
			disableClose: true,
			width: '450px',
			data: item,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
			this.notificationService.notify(['mes.scanning-prefix.notifications.delete.header'], ['mes.scanning-prefix.notifications.delete.content', { prefix: item.prefix }], 'success');
		});
	}
}
